/**
 * CompsApiList 通用API接口列表面板组件
 * 用于显示接口列表，支持分类分组和选择
 * 支持一级分类（可折叠）和二级分类
 */
import React, { useState } from "react";
import { List, Typography, Tag, Space, Card, Collapse } from "antd";
import { COMPONENT_SPLIT_BORDER_BOTTOM } from "../theme/componentBorders";
import { DownOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Panel } = Collapse;

// 方法颜色映射
const METHOD_COLORS: Record<string, string> = {
  GET: "blue",
  POST: "green",
  PUT: "orange",
  DELETE: "red",
  PATCH: "purple",
};

// 接口项类型定义
export interface ApiListItem {
  id: string;
  method: string;
  name: string;
  path: string;
}

// 分组数据类型定义（二级分类）
export interface ApiGroup {
  category: string;
  apis: ApiListItem[];
}

// 一级分类类型定义（可折叠的一级分类）
export interface ApiCategoryGroup {
  category: string; // 一级分类名称
  groups: ApiGroup[]; // 二级分类列表
}

// 组件属性类型定义
export interface CompsApiListProps {
  title?: string;
  groups?: ApiGroup[]; // 原来的方式（向后兼容）
  categoryGroups?: ApiCategoryGroup[]; // 新的一级分类方式
  selectedId?: string;
  onSelect: (id: string) => void;
  width?: number | string;
  height?: number | string;
}

const CompsApiList: React.FC<CompsApiListProps> = ({
  title = "接口列表",
  groups,
  categoryGroups,
  selectedId,
  onSelect,
  width = "400px",
  height = "100%",
}) => {
  // 展开的一级分类key（用于控制折叠状态）
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() => {
    // 默认展开所有一级分类
    if (categoryGroups) {
      return categoryGroups.map((_, index) => `category-${index}`);
    }
    return [];
  });

  // 渲染接口列表项
  const renderApiListItem = (api: ApiListItem) => (
    <List.Item
      onClick={() => onSelect(api.id)}
      style={{
        cursor: "pointer",
        backgroundColor: selectedId === api.id ? "#e6f7ff" : "transparent",
        borderLeft:
          selectedId === api.id ? "3px solid #1890ff" : "3px solid transparent",
        padding: "12px 16px",
        transition: "all 0.3s",
      }}
      onMouseEnter={(e) => {
        if (selectedId !== api.id) {
          e.currentTarget.style.backgroundColor = "#f5f5f5";
        }
      }}
      onMouseLeave={(e) => {
        if (selectedId !== api.id) {
          e.currentTarget.style.backgroundColor = "transparent";
        }
      }}
    >
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Space>
          <Tag color={METHOD_COLORS[api.method]}>{api.method}</Tag>
          <Text strong style={{ fontSize: "14px" }}>
            {api.name}
          </Text>
        </Space>
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {api.path}
        </Text>
      </Space>
    </List.Item>
  );

  // 渲染二级分类组（原来的方式）
  const renderGroup = ({ category, apis }: ApiGroup, key: string | number) => (
    <div key={key}>
      <div
        style={{
          padding: "8px 16px",
          backgroundColor: "#fafafa",
          borderBottom: COMPONENT_SPLIT_BORDER_BOTTOM,
          fontWeight: 600,
          fontSize: "14px",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        {category}
      </div>
      <List
        size="small"
        dataSource={apis}
        renderItem={renderApiListItem}
        split={false}
      />
    </div>
  );

  // 渲染一级分类（可折叠）
  const renderCategoryGroup = (
    categoryGroup: ApiCategoryGroup,
    index: number
  ) => {
    const panelKey = `category-${index}`;
    return (
      <Panel
        header={
          <span style={{ fontWeight: 600, fontSize: "15px" }}>
            {categoryGroup.category}
          </span>
        }
        key={panelKey}
      >
        {categoryGroup.groups.map((group, groupIndex) =>
          renderGroup(group, `${panelKey}-${groupIndex}`)
        )}
      </Panel>
    );
  };

  // 判断使用哪种显示方式
  const useCategoryGroups = categoryGroups && categoryGroups.length > 0;
  const useGroups = groups && groups.length > 0;

  return (
    <Card
      title={title}
      style={{
        width,
        height,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
      bodyStyle={{ flex: 1, overflow: "auto", padding: 0 }}
    >
      <div style={{ height: "100%", overflow: "auto" }}>
        {useCategoryGroups ? (
          // 使用一级分类（可折叠）方式
          <Collapse
            activeKey={expandedKeys}
            onChange={(keys) => setExpandedKeys(keys as string[])}
            ghost
            expandIcon={({ isActive }) => (
              <DownOutlined
                rotate={isActive ? 180 : 0}
                style={{ fontSize: "12px" }}
              />
            )}
            style={{ backgroundColor: "transparent" }}
          >
            {categoryGroups.map((categoryGroup, index) =>
              renderCategoryGroup(categoryGroup, index)
            )}
          </Collapse>
        ) : useGroups ? (
          // 使用原来的方式（向后兼容）
          groups.map((group, index) => renderGroup(group, index))
        ) : null}
      </div>
    </Card>
  );
};

export default CompsApiList;
